<template>
  <div class="carpool-page iabf-dancer-theme" :class="{ 'is-embedded': embedded }">
    <div class="carpool-container" :class="{ 'is-embedded': embedded }">
      <template v-if="!embedded">
        <router-link to="/" class="back-link">← Back to Dashboard</router-link>
        <h1 class="carpool-title">Airport Carpool</h1>
      </template>

      <div v-if="loading" class="loading-state">Loading...</div>

      <!-- SUMMARY MODE: already responded -->
      <div v-else-if="mode === 'summary'" class="summary-view">
        <div class="summary-card">
          <h2>Your Carpool Summary</h2>

          <div class="summary-row">
            <div class="summary-direction">Arrival</div>
            <div class="summary-detail">
              <span v-if="joinedArrivalSlot">
                <strong>{{ joinedArrivalSlot.airport }}</strong> · {{ joinedArrivalSlot.date }} · {{ joinedArrivalSlot.time }}
                <span v-if="isJoinedArrivalFree" class="summary-price summary-price-free">{{ freeRideLabel }}</span>
                <span v-else class="summary-price">${{ joinedArrivalSlot.pricePerPerson }}</span>
              </span>
              <span v-else class="not-booked">Not booked</span>
            </div>
          </div>

          <div class="summary-row">
            <div class="summary-direction">Departure</div>
            <div class="summary-detail">
              <span v-if="joinedDepartureSlot">
                <strong>{{ joinedDepartureSlot.airport }}</strong> · {{ joinedDepartureSlot.date }} · {{ joinedDepartureSlot.time }}
                <span v-if="isJoinedDepartureFree" class="summary-price summary-price-free">{{ freeRideLabel }}</span>
                <span v-else class="summary-price">${{ joinedDepartureSlot.pricePerPerson }}</span>
              </span>
              <span v-else class="not-booked">Not booked</span>
            </div>
          </div>

          <div v-if="joinedArrivalSlot || joinedDepartureSlot" class="summary-row summary-row-total">
            <div class="summary-direction">Total</div>
            <div class="summary-detail summary-total">
              <span v-if="isFaculty">Free</span>
              <span v-else-if="summaryFullyFree">
                <s v-if="summaryGross > 0" class="gift-strikethrough">${{ summaryGross }}</s>
                Free — thank you gift for returning to IABF
              </span>
              <span v-else-if="summaryPartiallyFree">
                <s class="gift-strikethrough">${{ summaryGross }}</s>
                ${{ summaryTotal }}
                <span class="gift-partial-note"> (1 way covered by gift)</span>
              </span>
              <span v-else>${{ summaryTotal }}</span>
            </div>
          </div>

          <div v-if="!joinedArrivalSlot && !joinedDepartureSlot" class="summary-policy">
            You are not signed up for a carpool.
            <template v-if="registrationClosed">
              Carpool signups are now closed — email
              <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> if you'd like one added.
            </template>
            <template v-else>Choose one below.</template>
          </div>

          <div v-if="selfServiceOpen" class="summary-actions">
            <button class="secondary-btn" :disabled="actionLoading" @click="changeBooking">
              Change or cancel your carpool
            </button>
          </div>
          <div v-else class="post-deadline-note">
            To make changes, email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>.
          </div>

          <div class="carpool-instructions">
            <p>
              You'll be connected with your carpool mates by text message
              <strong>the day before your carpool is scheduled</strong>.
              You'll have the chance to coordinate with each other as your
              flights arrive to make sure we don't leave anybody behind.
            </p>
            <p>
              If your flight is delayed, we may place you in a later car —
              but don't worry, we've got you!
            </p>
          </div>

          <div v-if="sameDayArrivalOptions.length" class="other-options">
            <h3 class="other-options-heading">Other arrivals on {{ joinedArrivalSlot.date }}</h3>
            <p class="other-options-sub">If your flight is delayed, here's what else we have running. Email hello@iabf.dance and we'll move you.</p>
            <ul class="other-options-list">
              <li v-for="opt in sameDayArrivalOptions" :key="opt.id">
                <strong>{{ opt.time }}</strong> · {{ opt.airport }}
              </li>
            </ul>
          </div>

          <div v-if="sameDayDepartureOptions.length" class="other-options">
            <h3 class="other-options-heading">Other departures on {{ joinedDepartureSlot.date }}</h3>
            <p class="other-options-sub">If you'd rather catch a different car, here's what else is running. Email hello@iabf.dance and we'll move you.</p>
            <ul class="other-options-list">
              <li v-for="opt in sameDayDepartureOptions" :key="opt.id">
                <strong>{{ opt.time }}</strong> · {{ opt.airport }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- PARTIAL MODE: admin assigned one direction, dancer needs to book the other -->
      <div v-else-if="mode === 'partial'" class="summary-view">
        <div class="summary-card">
          <h2>Your Carpool — Partially Booked</h2>
          <p class="partial-note">
            You've been registered for one carpool direction. Book the other below, or decline.
          </p>

          <div class="summary-row">
            <div class="summary-direction">Arrival</div>
            <div class="summary-detail">
              <span v-if="joinedArrivalSlot">
                <strong>{{ joinedArrivalSlot.airport }}</strong> · {{ joinedArrivalSlot.date }} · {{ joinedArrivalSlot.time }}
                <span v-if="isJoinedArrivalFree" class="summary-price summary-price-free">{{ freeRideLabel }}</span>
                <span v-else class="summary-price">${{ joinedArrivalSlot.pricePerPerson }}</span>
              </span>
              <span v-else class="not-booked">Not yet booked</span>
            </div>
          </div>

          <div class="summary-row">
            <div class="summary-direction">Departure</div>
            <div class="summary-detail">
              <span v-if="joinedDepartureSlot">
                <strong>{{ joinedDepartureSlot.airport }}</strong> · {{ joinedDepartureSlot.date }} · {{ joinedDepartureSlot.time }}
                <span v-if="isJoinedDepartureFree" class="summary-price summary-price-free">{{ freeRideLabel }}</span>
                <span v-else class="summary-price">${{ joinedDepartureSlot.pricePerPerson }}</span>
              </span>
              <span v-else class="not-booked">Not yet booked</span>
            </div>
          </div>

          <div class="partial-actions">
            <button class="primary-btn" @click="enterWizardFromPartial">
              Book {{ partialExistingDirection === 'arrival' ? 'departure' : 'arrival' }} carpool →
            </button>
          </div>
        </div>
      </div>

      <!-- WIZARD MODE -->
      <div v-else>

        <!-- STEP 1: Choose directions -->
        <div v-if="step === 1" class="wizard-step">
          <div class="step-indicator">Step 1 of {{ totalSteps }}</div>
          <h2>Would you like to sign up for a carpool?</h2>

          <div v-if="isFaculty" class="iabf-banner gift-banner">
            Airport carpools are complimentary for faculty.
          </div>
          <div v-else-if="hasCarpoolGift" class="iabf-banner gift-banner">
            <strong>Thank-you gift applied.</strong>
            {{ giftBannerText }}
          </div>
          <div v-else class="iabf-banner pricing-bar">
            <strong>Pricing (each way):</strong>
            Orlando MCO — ${{ carpoolPriceMco }}
            <span class="pricing-sep">·</span>
            Daytona Beach DAB — ${{ carpoolPriceDab }}
          </div>

          <p class="step-note">
            All carpools pick up and drop off at the Atlantic Center for the Arts or SpringHill Suites.
            <template v-if="!isFaculty">
              Your card on file will be charged automatically<template v-if="carpoolChargeLabel"> on {{ carpoolChargeLabel }}</template>.
            </template>
          </p>

          <div class="iabf-chips direction-pills" role="radiogroup" aria-label="Carpool direction">
            <button
              type="button"
              class="iabf-chip"
              role="radio"
              :aria-checked="directionChoice === 'both' ? 'true' : 'false'"
              title="Both arrival &amp; departure"
              :class="{ 'is-selected': directionChoice === 'both' }"
              @click="directionChoice = 'both'"
            >
              Round trip
            </button>
            <button
              type="button"
              class="iabf-chip"
              role="radio"
              :aria-checked="directionChoice === 'arrival' ? 'true' : 'false'"
              title="Getting to the festival"
              :class="{ 'is-selected': directionChoice === 'arrival' }"
              @click="directionChoice = 'arrival'"
            >
              Arrival only
            </button>
            <button
              type="button"
              class="iabf-chip"
              role="radio"
              :aria-checked="directionChoice === 'departure' ? 'true' : 'false'"
              title="Getting home"
              :class="{ 'is-selected': directionChoice === 'departure' }"
              @click="directionChoice = 'departure'"
            >
              Departure only
            </button>
            <button
              type="button"
              class="iabf-chip"
              role="radio"
              :aria-checked="directionChoice === 'neither' ? 'true' : 'false'"
              title="I'll get there on my own"
              :class="{ 'is-selected': directionChoice === 'neither' }"
              @click="directionChoice = 'neither'"
            >
              No carpool needed
            </button>
          </div>

          <div class="step-actions">
            <button type="button" class="iabf-btn iabf-btn--primary" :disabled="!directionChoice" @click="afterStep1">
              Continue →
            </button>
          </div>
        </div>

        <!-- STEP 2: Arrival slot -->
        <div v-else-if="step === 2" class="wizard-step">
          <div class="step-indicator">Step {{ wantDepartureCarpool ? '2 of ' + totalSteps : '2 of ' + totalSteps }}</div>
          <button class="back-btn" @click="goBackFromStep2">← Back</button>
          <h2>Arrival Carpool</h2>

          <div v-if="!noTravelDetails" class="info-card">
            <p>
              It looks like you are arriving from <strong>{{ arrivalAirportLabel }}</strong>
              and staying
              <strong>{{ isOnsite ? 'onsite' : 'offsite' }}</strong>.
            </p>
            <p class="dropoff-note">
              <span v-if="isOnsite">
                The carpool will pick you up at the airport and drop you off at the
                <strong>Atlantic Center for the Arts</strong>.
              </span>
              <span v-else>
                The carpool will pick you up at the airport and drop you off at the
                <strong>SpringHill Suites, New Smyrna Beach</strong>.
              </span>
            </p>
            <p v-if="travelDetails.arrivalTime" class="filter-note">
              Showing carpools that depart the airport after your flight lands at
              <strong>{{ travelDetails.arrivalTime }}</strong>.
            </p>
          </div>

          <h3 class="slots-heading">What time would you like to join the carpool?</h3>

          <div v-if="filteredArrivalSlots.length === 0" class="no-slots-msg">
            No carpools match your flight details. Contact
            <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> for help.
          </div>

          <div v-for="group in filteredArrivalGroups" :key="group.date" class="date-group">
            <h4 class="date-label">{{ group.date }}</h4>
            <div class="slots-grid">
              <template v-for="slot in group.slots">
                <div v-if="slot.isWaitlist" :key="slot.id" class="slot-card slot-waitlist">
                  <div class="waitlist-heading">Don't see a car that works for you?</div>
                  <div class="waitlist-sub">Join the waitlist for a car at your flight time. If we have enough interest, we will be happy to open a carpool for you.</div>
                  <div class="waitlist-time-row">
                    <label class="waitlist-time-label">What time would you like to leave?</label>
                    <input class="waitlist-time-input" type="text" v-model="arrivalWaitlistTime" placeholder="e.g. 4:30 PM">
                  </div>
                  <button
                    v-if="selectedArrivalSlot && selectedArrivalSlot.id === slot.id"
                    class="selected-btn"
                    @click="selectedArrivalSlot = null"
                  >✓ On waitlist — click to remove</button>
                  <button v-else class="choose-btn" @click="selectedArrivalSlot = slot">Join the Waitlist</button>
                </div>
                <div
                  v-else
                  :key="slot.id + '-r'"
                  class="slot-card"
                  :class="{
                    'slot-selected': selectedArrivalSlot && selectedArrivalSlot.id === slot.id,
                    'slot-full': slot.dancers.length >= slot.maxSeats && !(selectedArrivalSlot && selectedArrivalSlot.id === slot.id)
                  }"
                >
                  <div class="slot-top">
                    <span class="slot-airport">{{ slot.airport }}</span>
                    <span v-if="isSlotFree(slot, 'arrival')" class="slot-price slot-price-free">{{ freeRideLabel }}</span>
                    <span v-else class="slot-price">${{ slot.pricePerPerson }}</span>
                  </div>
                  <div class="slot-time">{{ slot.time }}</div>
                  <div v-if="arrivalArrivesText(slot)" class="slot-arrives">arrives venue ~{{ arrivalArrivesText(slot) }}</div>
                  <div class="slot-seats">
                    {{ slot.dancers.length }}/{{ slot.maxSeats }} dancers
                    <span v-if="slot.dancers.length >= slot.maxSeats" class="full-badge">Full</span>
                  </div>
                  <button
                    v-if="selectedArrivalSlot && selectedArrivalSlot.id === slot.id"
                    class="selected-btn"
                    @click="selectedArrivalSlot = null"
                  >✓ Selected — click to deselect</button>
                  <button
                    v-else-if="slot.dancers.length < slot.maxSeats"
                    class="choose-btn"
                    @click="selectedArrivalSlot = slot"
                  >Choose this carpool</button>
                  <span v-else class="full-label">Full</span>
                </div>
              </template>
            </div>
          </div>

          <div class="step-actions">
            <button
              class="primary-btn"
              :disabled="!selectedArrivalSlot"
              @click="afterStep2"
            >Continue →</button>
            <a class="nevermind-link" @click="skipArrival">Nevermind, I don't need an arrival carpool</a>
          </div>
        </div>

        <!-- STEP 3: Departure slot -->
        <div v-else-if="step === 3" class="wizard-step">
          <div class="step-indicator">Step {{ wantArrivalCarpool ? (totalSteps - 1) + ' of ' + totalSteps : '2 of ' + totalSteps }}</div>
          <button class="back-btn" @click="goBackFromStep3">← Back</button>
          <h2>Departure Carpool</h2>

          <div v-if="!noTravelDetails" class="info-card">
            <p>
              It looks like you are staying
              <strong>{{ isOnsite ? 'onsite' : 'offsite' }}</strong>
              and flying out of <strong>{{ departureAirportLabel }}</strong>.
              <span v-if="departureAirportDriveTime">This is approximately a <strong>{{ departureAirportDriveTime }}</strong> drive.</span>
            </p>
            <p class="dropoff-note">
              <span v-if="isOnsite">
                The carpool will pick you up at the
                <strong>Atlantic Center for the Arts</strong>.
              </span>
              <span v-else>
                The carpool will pick you up at the
                <strong>SpringHill Suites, New Smyrna Beach</strong>.
              </span>
            </p>
            <p v-if="travelDetails.departureTime" class="filter-note">
              Showing carpools that arrive at the airport at least
              <strong>{{ departureBufferLabel }}</strong> before your
              <strong>{{ travelDetails.departureTime }}</strong> flight.
            </p>
          </div>

          <h3 class="slots-heading">What time would you like to join the carpool?</h3>

          <div v-if="filteredDepartureSlots.length === 0" class="no-slots-msg">
            No carpools match your flight details. Contact
            <a href="mailto:hello@iabf.dance">hello@iabf.dance</a> for help.
          </div>

          <div v-for="group in filteredDepartureGroups" :key="group.date" class="date-group">
            <h4 class="date-label">{{ group.date }}</h4>
            <div class="slots-grid">
              <template v-for="slot in group.slots">
                <div v-if="slot.isWaitlist" :key="slot.id" class="slot-card slot-waitlist">
                  <div class="waitlist-heading">Don't see a car that works for you?</div>
                  <div class="waitlist-sub">Join the waitlist for a car at your flight time. If we have enough interest, we will be happy to open a carpool for you.</div>
                  <div class="waitlist-time-row">
                    <label class="waitlist-time-label">What time would you like to leave?</label>
                    <input class="waitlist-time-input" type="text" v-model="departureWaitlistTime" placeholder="e.g. 9:00 AM">
                  </div>
                  <button
                    v-if="selectedDepartureSlot && selectedDepartureSlot.id === slot.id"
                    class="selected-btn"
                    @click="selectedDepartureSlot = null"
                  >✓ On waitlist — click to remove</button>
                  <button v-else class="choose-btn" @click="selectedDepartureSlot = slot">Join the Waitlist</button>
                </div>
                <div
                  v-else
                  :key="slot.id + '-r'"
                  class="slot-card"
                  :class="{
                    'slot-selected': selectedDepartureSlot && selectedDepartureSlot.id === slot.id,
                    'slot-full': slot.dancers.length >= slot.maxSeats && !(selectedDepartureSlot && selectedDepartureSlot.id === slot.id)
                  }"
                >
                  <div class="slot-top">
                    <span class="slot-airport">{{ slot.airport }}</span>
                    <span v-if="isSlotFree(slot, 'departure')" class="slot-price slot-price-free">{{ freeRideLabel }}</span>
                    <span v-else class="slot-price">${{ slot.pricePerPerson }}</span>
                  </div>
                  <div class="slot-time">{{ slot.time }}</div>
                  <div v-if="departureArrivesText(slot)" class="slot-arrives">arrives airport ~{{ departureArrivesText(slot) }}</div>
                  <div class="slot-seats">
                    {{ slot.dancers.length }}/{{ slot.maxSeats }} dancers
                    <span v-if="slot.dancers.length >= slot.maxSeats" class="full-badge">Full</span>
                  </div>
                  <button
                    v-if="selectedDepartureSlot && selectedDepartureSlot.id === slot.id"
                    class="selected-btn"
                    @click="selectedDepartureSlot = null"
                  >✓ Selected — click to deselect</button>
                  <button
                    v-else-if="slot.dancers.length < slot.maxSeats"
                    class="choose-btn"
                    @click="selectedDepartureSlot = slot"
                  >Choose this carpool</button>
                  <span v-else class="full-label">Full</span>
                </div>
              </template>
            </div>
          </div>

          <div class="step-actions">
            <button
              class="primary-btn"
              :disabled="!selectedDepartureSlot"
              @click="afterStep3"
            >Continue →</button>
            <a class="nevermind-link" @click="skipDeparture">Nevermind, I don't need a departure carpool</a>
          </div>
        </div>

        <!-- STEP 4: Confirmation -->
        <div v-else-if="step === 4" class="wizard-step">
          <div class="step-indicator">Final step</div>
          <button class="back-btn" @click="goBackFromConfirmation">← Back</button>
          <h2>Last Step! Review your Selections</h2>

          <div class="confirmation-summary">
            <div class="confirm-row">
              <div class="confirm-label">Arrival</div>
              <div class="confirm-value">
                <span v-if="selectedArrivalSlot && !selectedArrivalSlot.isWaitlist">
                  <strong>{{ selectedArrivalSlot.airport }}</strong>
                  · {{ selectedArrivalSlot.date }}
                  · {{ selectedArrivalSlot.time }}
                  <span v-if="isSelectedArrivalFree"> · {{ freeRideLabel }}</span>
                  <span v-else> · ${{ selectedArrivalSlot.pricePerPerson }}</span>
                </span>
                <span v-else-if="selectedArrivalSlot && selectedArrivalSlot.isWaitlist">
                  <strong>{{ arrivalCode || 'Your airport' }}</strong>
                  · Waitlist<span v-if="arrivalWaitlistTime"> · Requested time: {{ arrivalWaitlistTime }}</span>
                </span>
                <span v-else class="confirm-none">Not booking an arrival carpool</span>
              </div>
            </div>
            <div class="confirm-row">
              <div class="confirm-label">Departure</div>
              <div class="confirm-value">
                <span v-if="selectedDepartureSlot && !selectedDepartureSlot.isWaitlist">
                  <strong>{{ selectedDepartureSlot.airport }}</strong>
                  · {{ selectedDepartureSlot.date }}
                  · {{ selectedDepartureSlot.time }}
                  <span v-if="isSelectedDepartureFree"> · {{ freeRideLabel }}</span>
                  <span v-else> · ${{ selectedDepartureSlot.pricePerPerson }}</span>
                </span>
                <span v-else-if="selectedDepartureSlot && selectedDepartureSlot.isWaitlist">
                  <strong>{{ departureCode || 'Your airport' }}</strong>
                  · Waitlist<span v-if="departureWaitlistTime"> · Requested time: {{ departureWaitlistTime }}</span>
                </span>
                <span v-else class="confirm-none">Not booking a departure carpool</span>
              </div>
            </div>
            <div v-if="selectedArrivalSlot || selectedDepartureSlot" class="confirm-row">
              <div class="confirm-label">Drop-off/pick-up</div>
              <div class="confirm-value">{{ isOnsite ? 'Atlantic Center for the Arts' : 'SpringHill Suites, New Smyrna Beach' }}</div>
            </div>
            <div v-if="!hasWaitlistOnly && directionChoice !== 'neither'" class="confirm-row confirm-row-total">
              <div class="confirm-label">Total</div>
              <div class="confirm-value confirm-total">
                <span v-if="isFaculty">Free</span>
                <span v-else-if="confirmationFullyFree">
                  <s v-if="confirmationGross > 0" class="gift-strikethrough">${{ confirmationGross }}</s>
                  Free — thank you gift for returning to IABF
                </span>
                <span v-else-if="confirmationPartiallyFree">
                  <s class="gift-strikethrough">${{ confirmationGross }}</s>
                  ${{ confirmationTotal }}
                  <span class="gift-partial-note"> (1 way covered by gift)</span>
                </span>
                <span v-else>${{ confirmationTotal }}</span>
              </div>
            </div>
          </div>

          <div v-if="hasWaitlistOnly" class="policy-box">
            If we have enough interest for a carpool at your requested time, we'll let you know<template v-if="carpoolChargeLabel"> by {{ carpoolChargeLabel }}</template>.
          </div>
          <div v-else-if="isFaculty && directionChoice !== 'neither'" class="policy-box">
            Carpools are not final and are subject to consolidation based on interest.
          </div>
          <div v-else-if="directionChoice !== 'neither' && confirmationFullyFree" class="policy-box">
            Carpools are not final and are subject to consolidation based on interest. Final carpools will be assigned<template v-if="carpoolChargeLabel"> on {{ carpoolChargeLabel }}</template>.
          </div>
          <div v-else-if="directionChoice !== 'neither'" class="policy-box">
            <template v-if="carpoolChargeLabel">
              No payment is due until {{ carpoolChargeLabel }}. Carpools are not final and are subject to consolidation based on interest. Final carpools will be assigned and automatically charged on {{ carpoolChargeLabel }}.
              You can cancel your carpool anytime until {{ carpoolChargeLabel }}, but we cannot offer refunds after that date.
            </template>
            <template v-else>
              Carpools are not final and are subject to consolidation based on interest. We'll charge the card on file when rides are confirmed.
            </template>
          </div>

          <p v-if="actionError" class="action-error" role="alert">{{ actionError }}</p>
          <div class="step-actions">
            <button class="primary-btn" :disabled="actionLoading" @click="confirmBooking">
              {{ actionLoading ? 'Saving...' : 'Book now' }}
            </button>
            <a v-if="directionChoice !== 'neither'" class="nevermind-link" @click="cancelWizard">
              Cancel and don't book
            </a>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import {
  isCarpoolRegistrationClosed,
  isCarpoolSelfServiceOpen
} from '@/config/iabf-registration-2027'
import { IABF_DANCER_YEAR } from '@/config/iabf-event'
import { effectiveDeadlineIso, formatDeadlineDisplay } from '@/config/iabf-deadlines'
import { carpoolDateSortKey } from '@/config/iabf-festival-dates'
import {
  CARPOOL_PRICES,
  carpoolGiftCreditsFromDancer,
  carpoolPriceAfterGift
} from '@/config/iabf-gifts'

export default {
  name: 'AirportCarpools',
  props: {
    /** When true, hide page chrome (used on the Travel page). */
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: true,
      actionLoading: false,
      actionError: '',
      mode: 'wizard',
      step: 1,
      directionChoice: 'both',
      selectedArrivalSlot: null,
      selectedDepartureSlot: null,
      prefilled: false,
      travelDetails: {},
      arrivalWaitlistTime: '',
      departureWaitlistTime: '',
      partialExistingDirection: null,
      enteredFromPartial: false
    }
  },
  computed: {
    carpoolPriceMco () {
      return CARPOOL_PRICES.MCO
    },
    carpoolPriceDab () {
      return CARPOOL_PRICES.DAB
    },
    festivalYear () {
      return Number(this.$store.getters.iabfDancerYear) ||
        Number(this.dancer.festivalYear) ||
        IABF_DANCER_YEAR
    },
    carpoolChargeLabel () {
      const iso = effectiveDeadlineIso('carpoolChange', this.festivalYear)
      if (!iso) {
        return ''
      }
      return formatDeadlineDisplay(iso)
    },
    registrationClosed () {
      return isCarpoolRegistrationClosed(this.festivalYear)
    },
    selfServiceOpen () {
      return isCarpoolSelfServiceOpen(this.festivalYear)
    },
    dancer () {
      return this.$store.getters.currentIABFDancer || {}
    },
    isFaculty () {
      const d = this.dancer || {}
      return !!(d.isFaculty || d.personType === 'faculty')
    },
    freeRideLabel () {
      return this.isFaculty ? 'Free' : 'Free (gift)'
    },
    slots () {
      // SFB is no longer offered — hide any leftover Sanford slots.
      return this.$store.getters.iabfCarpoolSlots.filter(s =>
        s.active && String(s.airport || '').toUpperCase() !== 'SFB'
      )
    },
    sameDayArrivalOptions () {
      if (!this.joinedArrivalSlot) return []
      const date = this.joinedArrivalSlot.date
      const mineId = this.joinedArrivalSlot.id
      return this.slots
        .filter(s => s.direction === 'arrival' && !s.isWaitlist && s.date === date && s.id !== mineId)
        .slice()
        .sort((a, b) => {
          const ta = this.parseTime(a.time)
          const tb = this.parseTime(b.time)
          return (ta === null ? Infinity : ta) - (tb === null ? Infinity : tb)
        })
    },
    sameDayDepartureOptions () {
      if (!this.joinedDepartureSlot) return []
      const date = this.joinedDepartureSlot.date
      const mineId = this.joinedDepartureSlot.id
      return this.slots
        .filter(s => s.direction === 'departure' && !s.isWaitlist && s.date === date && s.id !== mineId)
        .slice()
        .sort((a, b) => {
          const ta = this.parseTime(a.time)
          const tb = this.parseTime(b.time)
          return (ta === null ? Infinity : ta) - (tb === null ? Infinity : tb)
        })
    },
    joinedArrivalSlot () {
      // The slot's dancers[] array is the source of truth — when the admin
      // adds someone via the carpools page, the dancer doc's denormalized
      // carpoolArrivalSlotId can lag. Find the slot by membership instead so
      // the dancer always sees what they're actually in.
      if (!this.dancer || !this.dancer.id) return null
      const byMembership = this.slots.find(s =>
        s.direction === 'arrival' && !s.isWaitlist &&
        (s.dancers || []).some(d => d.id === this.dancer.id)
      )
      if (byMembership) return byMembership
      return this.slots.find(s => s.id === this.dancer.carpoolArrivalSlotId) || null
    },
    joinedDepartureSlot () {
      if (!this.dancer || !this.dancer.id) return null
      const byMembership = this.slots.find(s =>
        s.direction === 'departure' && !s.isWaitlist &&
        (s.dancers || []).some(d => d.id === this.dancer.id)
      )
      if (byMembership) return byMembership
      return this.slots.find(s => s.id === this.dancer.carpoolDepartureSlotId) || null
    },
    carpoolGiftCredits () {
      return carpoolGiftCreditsFromDancer(this.dancer)
    },
    /** True if they have any carpool gift credit at all. */
    hasCarpoolGift () {
      return this.carpoolGiftCredits > 0
    },
    /** @deprecated use hasCarpoolGift / isArrivalFree / isDepartureFree */
    hasFreeCarpoolGift () {
      return this.hasCarpoolGift
    },
    joinedPriceBreakdown () {
      return carpoolPriceAfterGift({
        arrivalSlot: this.joinedArrivalSlot,
        departureSlot: this.joinedDepartureSlot,
        credits: this.carpoolGiftCredits
      })
    },
    selectedPriceBreakdown () {
      return carpoolPriceAfterGift({
        arrivalSlot: this.selectedArrivalSlot,
        departureSlot: this.selectedDepartureSlot,
        credits: this.carpoolGiftCredits
      })
    },
    isJoinedArrivalFree () {
      return this.joinedPriceBreakdown.freeArrival
    },
    isJoinedDepartureFree () {
      return this.joinedPriceBreakdown.freeDeparture
    },
    isSelectedArrivalFree () {
      return this.selectedPriceBreakdown.freeArrival
    },
    isSelectedDepartureFree () {
      return this.selectedPriceBreakdown.freeDeparture
    },
    giftBannerText () {
      const n = this.carpoolGiftCredits
      if (n <= 0) return ''
      if (n >= 2) {
        return 'Your airport carpool is complimentary both ways — thank you for returning to IABF!'
      }
      return 'It covers 1-way MCO or a full Daytona (DAB) roundtrip — thank you for returning to IABF!'
    },
    isOnsite () {
      return ['Standard Dorm', 'ADA Dorm', 'Cottage', 'Staying with another dancer'].includes(this.dancer.room)
    },
    arrivalCode () {
      return this.extractAirportCode(this.travelDetails.arrivalAirport || '')
    },
    departureCode () {
      return this.extractAirportCode(this.travelDetails.departureAirport || '')
    },
    arrivalAirportLabel () {
      return this.travelDetails.arrivalAirport || this.arrivalCode || 'your airport'
    },
    departureAirportLabel () {
      return this.travelDetails.departureAirport || this.departureCode || 'your airport'
    },
    departureAirportDriveTime () {
      return { MCO: '1 hour 15 minutes', SFB: '45 minutes', DAB: '25 minutes' }[this.departureCode] || null
    },
    departureBufferMins () {
      return { MCO: 180, SFB: 165, DAB: 150 }[this.departureCode] || 180
    },
    departureBufferLabel () {
      return { MCO: '3 hours', SFB: '2 hours 45 minutes', DAB: '2.5 hours' }[this.departureCode] || '3 hours'
    },
    wantArrivalCarpool () {
      return this.directionChoice === 'arrival' || this.directionChoice === 'both'
    },
    wantDepartureCarpool () {
      return this.directionChoice === 'departure' || this.directionChoice === 'both'
    },
    totalSteps () {
      if (this.directionChoice === 'both') return 4
      if (this.directionChoice === 'neither') return 2
      return 3
    },
    filteredArrivalSlots () {
      const code = this.arrivalCode
      const landingMins = this.parseTime(this.travelDetails.arrivalTime)
      const monthDay = this.isoToMonthDay(this.travelDetails.arrivalDate)
      return this.slots.filter(s => {
        if (s.direction !== 'arrival') return false
        if (!s.isWaitlist && code && s.airport !== code) return false
        if (!s.isWaitlist && monthDay && !s.date.includes(monthDay)) return false
        if (!s.isWaitlist && landingMins !== null) {
          const slotMins = this.parseTime(s.time)
          if (slotMins !== null && slotMins < landingMins + 45) return false
        }
        return true
      })
    },
    filteredArrivalGroups () {
      return this.groupSlots(this.filteredArrivalSlots)
    },
    filteredDepartureSlots () {
      const code = this.departureCode
      const flightMins = this.parseTime(this.travelDetails.departureTime)
      const buffer = this.departureBufferMins
      const monthDay = this.isoToMonthDay(this.travelDetails.departureDate)
      return this.slots.filter(s => {
        if (s.direction !== 'departure') return false
        if (!s.isWaitlist && code && s.airport !== code) return false
        if (!s.isWaitlist && monthDay && !s.date.includes(monthDay)) return false
        if (!s.isWaitlist && flightMins !== null) {
          const leavesMins = this.parseTime(s.time)
          if (leavesMins !== null && leavesMins + buffer > flightMins) return false
        }
        return true
      })
    },
    filteredDepartureGroups () {
      return this.groupSlots(this.filteredDepartureSlots)
    },
    noTravelDetails () {
      return Object.keys(this.travelDetails).length === 0
    },
    // Kept for any remaining template refs; prefer selfServiceOpen.
    isBeforeJuly1 () {
      return this.selfServiceOpen
    },
    summaryGross () {
      return this.joinedPriceBreakdown.gross
    },
    summaryTotal () {
      return this.joinedPriceBreakdown.total
    },
    confirmationGross () {
      return this.selectedPriceBreakdown.gross
    },
    confirmationTotal () {
      return this.selectedPriceBreakdown.total
    },
    summaryFullyFree () {
      return this.joinedPriceBreakdown.gross > 0 && this.joinedPriceBreakdown.total === 0
    },
    confirmationFullyFree () {
      return this.selectedPriceBreakdown.gross > 0 && this.selectedPriceBreakdown.total === 0
    },
    summaryPartiallyFree () {
      return this.joinedPriceBreakdown.gross > this.joinedPriceBreakdown.total &&
        this.joinedPriceBreakdown.total > 0
    },
    confirmationPartiallyFree () {
      return this.selectedPriceBreakdown.gross > this.selectedPriceBreakdown.total &&
        this.selectedPriceBreakdown.total > 0
    },
    allWaitlist () {
      const a = this.selectedArrivalSlot
      const d = this.selectedDepartureSlot
      if (!a && !d) return false
      if (a && !a.isWaitlist) return false
      if (d && !d.isWaitlist) return false
      return true
    },
    hasWaitlistOnly () {
      return this.allWaitlist
    }
  },
  watch: {
    dancer: {
      immediate: true,
      async handler (val) {
        if (!val || !val.id || this.prefilled || this.loading) {
          return
        }
        await this.prefillFromDancer(val)
      }
    }
  },
  async created () {
    if (!this.embedded) {
      this.$store.commit('setLayout', 'iabf-layout')
    }
    const dancer = this.$store.getters.currentIABFDancer
    await this.$store.dispatch('loadCarpoolSlots')
    if (dancer && !this.prefilled) {
      await this.prefillFromDancer(dancer)
    }
    this.loading = false
  },
  methods: {
    async prefillFromDancer (dancer) {
      this.prefilled = true
      if (dancer.travelDetailsId) {
        await this.$store.dispatch('loadIABFTravelDetails', dancer.travelDetailsId)
        this.travelDetails = { ...this.$store.getters.loadedTravelDetails }
      }
      // Registration closed: everyone lands on the summary view.
      // No wizard or partial-flow routing — admins handle changes by hand.
      if (this.registrationClosed) {
        this.mode = 'summary'
        return
      }
      // Open: route based on prior response or actual slot membership
      // (masquerade can lag the dancer snapshot after a booking).
      const hasArrival = !!(this.joinedArrivalSlot || dancer.carpoolArrivalSlotId)
      const hasDeparture = !!(this.joinedDepartureSlot || dancer.carpoolDepartureSlotId)
      if (dancer.carpoolResponded || hasArrival || hasDeparture) {
        const partial = (hasArrival !== hasDeparture) &&
          !dancer.carpoolArrivalDeclined &&
          !dancer.carpoolDepartureDeclined
        if (partial) {
          this.mode = 'partial'
          this.partialExistingDirection = hasArrival ? 'arrival' : 'departure'
        } else {
          this.mode = 'summary'
        }
      }
    },

    afterStep1 () {
      if (this.directionChoice === 'arrival') this.step = 2
      else if (this.directionChoice === 'departure') this.step = 3
      else if (this.directionChoice === 'both') this.step = 2
      else this.step = 4 // neither
    },

    afterStep2 () {
      if (this.wantDepartureCarpool) this.step = 3
      else this.step = 4
    },

    skipArrival () {
      this.selectedArrivalSlot = null
      if (this.wantDepartureCarpool) this.step = 3
      else this.step = 4
    },

    afterStep3 () {
      this.step = 4
    },

    skipDeparture () {
      this.selectedDepartureSlot = null
      this.step = 4
    },

    goBackFromConfirmation () {
      if (this.wantDepartureCarpool) this.step = 3
      else if (this.wantArrivalCarpool) this.step = 2
      else this.step = 1
    },

    goBackFromStep2 () {
      if (this.enteredFromPartial) {
        this.mode = 'partial'
        this.enteredFromPartial = false
        this.selectedArrivalSlot = null
        this.selectedDepartureSlot = null
      } else {
        this.step = 1
      }
    },

    goBackFromStep3 () {
      if (this.enteredFromPartial) {
        this.mode = 'partial'
        this.enteredFromPartial = false
        this.selectedArrivalSlot = null
        this.selectedDepartureSlot = null
      } else {
        this.step = this.wantArrivalCarpool ? 2 : 1
      }
    },

    cancelWizard () {
      if (this.enteredFromPartial) {
        this.mode = 'partial'
        this.enteredFromPartial = false
        this.selectedArrivalSlot = null
        this.selectedDepartureSlot = null
      } else {
        this.step = 1
        this.selectedArrivalSlot = null
        this.selectedDepartureSlot = null
        this.directionChoice = 'both'
      }
    },

    enterWizardFromPartial () {
      const missing = this.partialExistingDirection === 'arrival' ? 'departure' : 'arrival'
      // Pre-populate the already-booked direction so confirmation shows both
      if (this.partialExistingDirection === 'arrival') {
        this.selectedArrivalSlot = this.joinedArrivalSlot
      } else {
        this.selectedDepartureSlot = this.joinedDepartureSlot
      }
      this.directionChoice = missing
      this.enteredFromPartial = true
      this.mode = 'wizard'
      this.step = missing === 'arrival' ? 2 : 3
    },

    async confirmBooking () {
      this.actionLoading = true
      this.actionError = ''
      try {
        const dancer = this.dancer
        const dropoffPickup = this.isOnsite ? 'atlantic' : 'salty_mermaid'
        const name = (dancer.firstName || '') + ' ' + ((dancer.lastName || '')[0] || '') + '.'

        // Skip joinCarpoolSlot for the direction already assigned by admin (partial mode)
        if (this.selectedArrivalSlot && this.partialExistingDirection !== 'arrival') {
          await this.$store.dispatch('joinCarpoolSlot', {
            slotId: this.selectedArrivalSlot.id,
            direction: 'arrival',
            dancerId: dancer.id,
            dancerName: name,
            carpoolArrivalDropoff: dropoffPickup,
            requestedTime: this.selectedArrivalSlot.isWaitlist ? this.arrivalWaitlistTime : '',
            airport: this.selectedArrivalSlot.isWaitlist ? this.arrivalCode : undefined
          })
        }

        if (this.selectedDepartureSlot && this.partialExistingDirection !== 'departure') {
          await this.$store.dispatch('joinCarpoolSlot', {
            slotId: this.selectedDepartureSlot.id,
            direction: 'departure',
            dancerId: dancer.id,
            dancerName: name,
            carpoolDeparturePickup: dropoffPickup,
            requestedTime: this.selectedDepartureSlot.isWaitlist ? this.departureWaitlistTime : '',
            airport: this.selectedDepartureSlot.isWaitlist ? this.departureCode : undefined
          })
        }

        const update = { carpoolResponded: true }
        if (!this.selectedArrivalSlot && this.partialExistingDirection !== 'arrival') update.carpoolArrivalDeclined = true
        if (!this.selectedDepartureSlot && this.partialExistingDirection !== 'departure') update.carpoolDepartureDeclined = true

        await this.$store.dispatch('updateDancerCarpoolDecline', { dancerId: dancer.id, update })
        await this.$store.dispatch('loadCarpoolSlots')

        this.$store.commit('patchEffectiveIABFDancer', {
          id: dancer.id,
          ...update,
          carpoolArrivalSlotId: this.selectedArrivalSlot ? this.selectedArrivalSlot.id : (dancer.carpoolArrivalSlotId || null),
          carpoolDepartureSlotId: this.selectedDepartureSlot ? this.selectedDepartureSlot.id : (dancer.carpoolDepartureSlotId || null)
        })

        this.partialExistingDirection = null
        this.enteredFromPartial = false
        this.mode = 'summary'
      } catch (e) {
        console.error('confirmBooking failed:', e)
        this.actionError = 'Could not save your carpool booking. Please try again.'
      } finally {
        this.actionLoading = false
      }
    },

    async changeBooking () {
      this.actionLoading = true
      this.actionError = ''
      try {
        const dancer = this.dancer

        // Save previous slot IDs for pre-filling wizard
        const prevArrivalId = dancer.carpoolArrivalSlotId || null
        const prevDepartureId = dancer.carpoolDepartureSlotId || null

        if (dancer.carpoolArrivalSlotId) {
          await this.$store.dispatch('leaveCarpoolSlot', {
            slotId: dancer.carpoolArrivalSlotId, direction: 'arrival', dancerId: dancer.id
          })
        }
        if (dancer.carpoolDepartureSlotId) {
          await this.$store.dispatch('leaveCarpoolSlot', {
            slotId: dancer.carpoolDepartureSlotId, direction: 'departure', dancerId: dancer.id
          })
        }

        await this.$store.dispatch('updateDancerCarpoolDecline', {
          dancerId: dancer.id,
          update: {
            carpoolResponded: false,
            carpoolArrivalSlotId: null,
            carpoolDepartureSlotId: null,
            carpoolArrivalDeclined: false,
            carpoolDepartureDeclined: false
          }
        })

        await this.$store.dispatch('loadCarpoolSlots')

        this.$store.commit('setCurrentIABFDancer', {
          ...dancer,
          carpoolResponded: false,
          carpoolArrivalSlotId: null,
          carpoolDepartureSlotId: null,
          carpoolArrivalDeclined: false,
          carpoolDepartureDeclined: false
        })

        // Pre-select previously chosen slots in the wizard
        const freshSlots = this.$store.getters.iabfCarpoolSlots.filter(s => s.active)
        const prevArrival = prevArrivalId ? (freshSlots.find(s => s.id === prevArrivalId) || null) : null
        const prevDeparture = prevDepartureId ? (freshSlots.find(s => s.id === prevDepartureId) || null) : null
        this.selectedArrivalSlot = prevArrival
        this.selectedDepartureSlot = prevDeparture
        if (prevArrival && prevDeparture) this.directionChoice = 'both'
        else if (prevArrival) this.directionChoice = 'arrival'
        else if (prevDeparture) this.directionChoice = 'departure'
        else this.directionChoice = ''

        this.partialExistingDirection = null
        this.enteredFromPartial = false
        this.mode = 'wizard'
        this.step = 1
      } catch (e) {
        console.error('changeBooking failed:', e)
        this.actionError = 'Could not update your carpool booking. Please try again.'
      } finally {
        this.actionLoading = false
      }
    },

    /**
     * Whether a slot card would be free under the dancer's gift credits,
     * given the currently selected other direction (if any).
     */
    isSlotFree (slot, direction) {
      if (!slot || slot.isWaitlist || this.carpoolGiftCredits <= 0) {
        return false
      }
      const arrivalSlot = direction === 'arrival' ? slot : this.selectedArrivalSlot
      const departureSlot = direction === 'departure' ? slot : this.selectedDepartureSlot
      const breakdown = carpoolPriceAfterGift({
        arrivalSlot,
        departureSlot,
        credits: this.carpoolGiftCredits
      })
      return direction === 'arrival' ? breakdown.freeArrival : breakdown.freeDeparture
    },
    // Helpers
    extractAirportCode (label) {
      if (!label) return ''
      const m = label.match(/\((\w+)\)/)
      if (m) return m[1].toUpperCase()
      const lower = label.toLowerCase()
      if (lower.includes('orlando') || lower.includes('mco')) return 'MCO'
      if (lower.includes('daytona') || lower.includes('dab')) return 'DAB'
      if (lower.includes('sanford') || lower.includes('sfb')) return 'SFB'
      return ''
    },
    isoToMonthDay (isoDate) {
      if (!isoDate) return null
      const d = new Date(isoDate + 'T12:00:00')
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      return months[d.getMonth()] + ' ' + d.getDate()
    },
    parseTime (str) {
      if (!str) return null
      const s = str.toString().trim().toLowerCase()
      // HH:MM am/pm format (e.g. "3:00 PM", "11:00 AM")
      let m = s.match(/(\d{1,2}):(\d{2})\s*(am|pm)?/)
      if (m) {
        let h = parseInt(m[1])
        const min = parseInt(m[2])
        const ap = m[3]
        if (ap === 'pm' && h < 12) h += 12
        if (ap === 'am' && h === 12) h = 0
        return h * 60 + min
      }
      // Bare hour format (e.g. "3pm", "10am", "3 pm")
      m = s.match(/^(\d{1,2})\s*(am|pm)/)
      if (m) {
        let h = parseInt(m[1])
        const ap = m[2]
        if (ap === 'pm' && h < 12) h += 12
        if (ap === 'am' && h === 12) h = 0
        return h * 60
      }
      return null
    },
    driveTimeMins (airport) {
      return { MCO: 75, SFB: 45, DAB: 25 }[airport] || 0
    },
    formatMins (totalMins) {
      const h24 = Math.floor(totalMins / 60)
      const min = totalMins % 60
      const h12 = h24 % 12 === 0 ? 12 : h24 % 12
      const ap = h24 < 12 ? 'am' : 'pm'
      return h12 + ':' + String(min).padStart(2, '0') + ap
    },
    arrivalArrivesText (slot) {
      const departsMins = this.parseTime(slot.time)
      if (departsMins === null) return null
      return this.formatMins(departsMins + this.driveTimeMins(slot.airport))
    },
    departureArrivesText (slot) {
      const leavesMins = this.parseTime(slot.time)
      if (leavesMins === null) return null
      return this.formatMins(leavesMins + this.driveTimeMins(slot.airport))
    },
    groupSlots (slots) {
      const groups = []
      const regular = slots.filter(s => !s.isWaitlist)
      const waitlist = slots.filter(s => s.isWaitlist)
      const seen = {}
      regular.forEach(s => {
        // Normalize whitespace so trivial differences in the stored date
        // string don't create a duplicate group below the later dates.
        const key = String(s.date || '').trim().replace(/\s+/g, ' ')
        if (!seen[key]) {
          seen[key] = { date: key, slots: [], sortKey: this.dateSortKey(key) }
          groups.push(seen[key])
        }
        seen[key].slots.push(s)
      })
      groups.sort((a, b) => a.sortKey - b.sortKey)
      groups.forEach(g => {
        g.slots.sort((a, b) => {
          const at = this.parseTime(a.time)
          const bt = this.parseTime(b.time)
          return (at === null ? Infinity : at) - (bt === null ? Infinity : bt)
        })
      })
      if (waitlist.length > 0) groups.push({ date: 'Waitlist', slots: waitlist, sortKey: Infinity })
      return groups
    },
    dateSortKey (dateStr) {
      return carpoolDateSortKey(dateStr, this.festivalYear)
    }
  }
}
</script>

<style scoped>
.carpool-page {
  background: var(--iabf-bg, #f7f6f4);
  min-height: 100vh;
  padding: 40px 16px 80px;
}
.carpool-page.is-embedded {
  background: transparent;
  min-height: 0;
  padding: 0;
}
.carpool-container {
  max-width: 700px;
  margin: 0 auto;
}
.carpool-container.is-embedded {
  max-width: none;
  margin: 0;
}
.back-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  text-decoration: none;
  display: inline-block;
  margin-bottom: 24px;
}
.back-link:hover {
  color: var(--iabf-ink, #1c1b1f);
}
.carpool-title,
h1 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 36px;
  font-weight: 400;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0 0 28px;
  line-height: 1.15;
  letter-spacing: -0.02em;
}
h2 {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 26px;
  font-weight: 400;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0 0 18px;
  line-height: 1.25;
  letter-spacing: -0.01em;
}
h3.slots-heading {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 18px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
  margin: 24px 0 12px;
}

/* Step indicator */
.step-indicator {
  font-family: var(--iabf-font-body, inherit);
  font-size: 12px;
  font-weight: 700;
  color: var(--iabf-primary, #8a957d);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 12px;
}

/* Wizard step container — elevated white card */
.wizard-step {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 28px 24px 24px;
}

/* Gift / pricing banners — white elevated cards, sage accent text */
.gift-banner,
.pricing-bar {
  margin-bottom: 16px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--iabf-ink, #1c1b1f);
}
.gift-banner strong {
  color: var(--iabf-primary, #8a957d);
  display: block;
  margin-bottom: 4px;
  font-weight: 700;
}
.pricing-bar strong {
  color: var(--iabf-ink, #1c1b1f);
}
.pricing-sep {
  margin: 0 6px;
  color: var(--iabf-ink-faint, #8a8792);
}

.step-note {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  margin: 0 0 24px;
  line-height: 1.55;
}

/* Direction choice pills (match lodging trip chips) */
.direction-pills {
  margin: 0 0 28px;
}
.direction-pills .iabf-chip {
  font-weight: 600;
}

/* Info card for arrival/departure steps */
.info-card {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04);
  padding: 16px 18px;
  margin-bottom: 20px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.55;
}
.info-card p { margin: 0 0 8px; }
.info-card p:last-child { margin-bottom: 0; }
.dropoff-note { color: var(--iabf-ink, #1c1b1f); }
.filter-note { color: var(--iabf-ink-faint, #8a8792); font-size: 14px; }

/* Slot grid */
.date-group { margin-bottom: 20px; }
h4.date-label {
  font-family: var(--iabf-font-body, inherit);
  font-size: 12px;
  font-weight: 700;
  color: var(--iabf-ink-faint, #8a8792);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  margin: 0 0 10px;
}
.slots-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.slot-card {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  padding: 14px 16px;
  min-width: 165px;
  max-width: 200px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.03);
}
.slot-card.slot-selected {
  border-color: var(--iabf-primary, #8a957d);
  background: rgba(138, 149, 125, 0.1);
  box-shadow: 0 0 0 1px var(--iabf-primary, #8a957d);
}
.slot-card.slot-full { opacity: 0.5; }
.slot-card.slot-waitlist {
  border-style: dashed;
  max-width: 100%;
  width: 100%;
}
.slot-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.slot-airport {
  font-family: var(--iabf-font-body, inherit);
  font-size: 11px;
  font-weight: 700;
  color: var(--iabf-ink-faint, #8a8792);
  letter-spacing: 0.08em;
}
.slot-price {
  font-family: var(--iabf-font-body, inherit);
  font-size: 12px;
  color: #777;
}
.slot-price-free {
  color: var(--iabf-primary, #8a957d);
  font-weight: 700;
}
.free-tag { color: var(--iabf-primary, #8a957d); font-weight: 700; }
.slot-time {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 18px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
}
.slot-arrives {
  font-family: var(--iabf-font-body, inherit);
  font-size: 11px;
  color: var(--iabf-ink-faint, #8a8792);
  margin-top: 1px;
}
.slot-seats {
  font-family: var(--iabf-font-body, inherit);
  font-size: 12px;
  color: var(--iabf-ink-faint, #8a8792);
}
.waitlist-heading {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 16px;
  font-weight: 700;
  color: var(--iabf-ink, #1c1b1f);
  margin-bottom: 6px;
}
.waitlist-sub {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: #777;
  line-height: 1.5;
  margin-bottom: 14px;
}
.waitlist-time-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
}
.waitlist-time-label {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: #555;
  font-weight: 700;
}
.waitlist-time-input {
  width: 180px;
  min-height: 44px;
  padding: 10px 12px;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
  background: #fff;
}
.waitlist-time-input:focus {
  outline: none;
  border-color: var(--iabf-primary, #8a957d);
  box-shadow: 0 0 0 3px var(--iabf-focus-ring, rgba(138, 149, 125, 0.28));
}
.full-badge {
  background: rgba(181, 74, 74, 0.12);
  color: var(--iabf-danger, #b54a4a);
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  margin-left: 4px;
}
.slot-names {
  font-family: var(--iabf-font-body, inherit);
  font-size: 12px;
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}
.choose-btn {
  margin-top: 10px;
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 8px 14px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  align-self: flex-start;
}
.choose-btn:hover {
  background: var(--iabf-primary-pressed, #6f7a64);
}
.selected-btn {
  margin-top: 10px;
  background: #fff;
  color: var(--iabf-primary, #8a957d);
  border: 1.5px solid var(--iabf-primary, #8a957d);
  border-radius: 10px;
  padding: 7px 12px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  align-self: flex-start;
}
.full-label {
  font-family: var(--iabf-font-body, inherit);
  font-size: 12px;
  color: var(--iabf-ink-faint, #8a8792);
  margin-top: 8px;
}

/* Step actions */
.step-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 24px;
}
.primary-btn,
.iabf-btn--primary.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  background: var(--iabf-primary, #8a957d);
  color: #fff;
  border: 1.5px solid var(--iabf-primary, #8a957d);
  border-radius: 12px;
  padding: 12px 22px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.primary-btn:hover:not(:disabled) {
  background: var(--iabf-primary-pressed, #6f7a64);
  border-color: var(--iabf-primary-pressed, #6f7a64);
}
.primary-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.action-error {
  color: var(--iabf-danger, #b54a4a);
  font-size: 15px;
  margin: 0 0 12px;
  line-height: 1.4;
}
.secondary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  background: #fff;
  color: var(--iabf-ink, #1c1b1f);
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 12px;
  padding: 12px 20px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}
.secondary-btn:hover:not(:disabled) {
  background: var(--iabf-bg-soft, #eef0eb);
}
.back-btn {
  background: transparent;
  border: none;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  font-weight: 600;
  color: var(--iabf-primary, #8a957d);
  cursor: pointer;
  padding: 0;
  margin-bottom: 14px;
  display: block;
}
.nevermind-link {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  font-weight: 600;
  color: var(--iabf-ink-muted, #5c5a63);
  text-decoration: underline;
  cursor: pointer;
}
.no-slots-msg {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-muted, #5c5a63);
  padding: 16px 0;
}
.no-slots-msg a {
  color: var(--iabf-primary, #8a957d);
  font-weight: 600;
}

/* Confirmation step */
.confirmation-summary {
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 20px;
  background: #fff;
}
.confirm-row {
  display: flex;
  gap: 16px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
  font-family: var(--iabf-font-body, inherit);
}
.confirm-row:last-child { border-bottom: none; }
.confirm-label {
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  min-width: 90px;
  padding-top: 2px;
}
.confirm-value {
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
  flex: 1;
  line-height: 1.5;
}
.confirm-none { color: var(--iabf-ink-faint, #8a8792); font-style: italic; }
.confirm-row-total {
  border-top: 1px solid #e0ddd9;
  margin-top: 2px;
}
.confirm-total {
  font-weight: 700;
  font-size: 16px;
}
.policy-box {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 14px;
  padding: 14px 16px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.6;
  margin-bottom: 20px;
}

/* Summary mode */
.summary-card {
  background: #fff;
  border: 1.5px solid var(--iabf-border, #e3e0e8);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(28, 27, 31, 0.04), 0 8px 24px rgba(28, 27, 31, 0.06);
  padding: 28px 24px;
}
.summary-card h2 {
  margin-bottom: 20px;
}
.summary-row {
  display: flex;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--iabf-border, #e3e0e8);
  font-family: var(--iabf-font-body, inherit);
}
.summary-row:last-of-type { border-bottom: none; }
.summary-row-total {
  border-top: 1px solid #e0ddd9;
}
.summary-total {
  font-weight: 700;
  font-size: 16px;
}
.summary-policy {
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink-muted, #5c5a63);
  line-height: 1.6;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--iabf-border, #e3e0e8);
}
.summary-direction {
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  min-width: 90px;
  padding-top: 2px;
}
.summary-detail {
  font-size: 15px;
  color: var(--iabf-ink, #1c1b1f);
  flex: 1;
  line-height: 1.5;
}
.summary-price {
  color: #555;
  font-size: 13px;
  margin-left: 4px;
}
.summary-price-free {
  color: var(--iabf-primary, #8a957d);
  font-weight: 700;
}
.summary-free {
  color: var(--iabf-primary, #8a957d);
  font-size: 13px;
  font-weight: 700;
  margin-left: 4px;
}
.gift-strikethrough {
  color: var(--iabf-ink-faint, #8a8792);
  font-weight: 400;
  margin-right: 6px;
}
.gift-partial-note {
  font-size: 13px;
  font-weight: 400;
  color: var(--iabf-ink-faint, #8a8792);
}
.not-booked {
  color: var(--iabf-ink-faint, #8a8792);
  font-style: italic;
}
.summary-actions {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}
.policy-inline {
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  color: var(--iabf-ink-faint, #8a8792);
  margin: 0;
}
.post-deadline-note {
  margin-top: 20px;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
  color: var(--iabf-ink-faint, #8a8792);
  line-height: 1.6;
}
.post-deadline-note a { color: #555; }

.other-options {
  margin-top: 22px;
  padding: 14px 16px;
  background: #f7f5ef;
  border-radius: 8px;
}

.other-options-heading {
  font-family: var(--iabf-font-display, Georgia, serif);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 4px;
  color: var(--iabf-ink, #1c1b1f);
}

.other-options-sub {
  font-family: var(--iabf-font-body, inherit);
  font-size: 13px;
  color: #555;
  margin: 0 0 8px;
}

.other-options-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-family: var(--iabf-font-body, inherit);
  font-size: 14px;
}

.other-options-list li {
  padding: 4px 0;
  border-bottom: 1px dashed #e2dec9;
}

.other-options-list li:last-child {
  border-bottom: none;
}

.carpool-instructions {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eee5d6;
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: #4a3700;
  line-height: 1.55;
}

.carpool-instructions p {
  margin: 0 0 12px;
}

.carpool-instructions p:last-child {
  margin-bottom: 0;
}

/* Partial mode */
.partial-note {
  font-family: var(--iabf-font-body, inherit);
  font-size: 15px;
  color: var(--iabf-ink-faint, #8a8792);
  margin-bottom: 8px;
  line-height: 1.5;
}
.partial-actions {
  margin-top: 24px;
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 60px 0;
  font-family: var(--iabf-font-body, inherit);
  color: var(--iabf-ink-faint, #8a8792);
}
</style>
